/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  SOFT PROMPT — cadence générique des invitations en-app (socle partagé)
 * ─────────────────────────────────────────────────────────────────────────────
 *  Moteur commun derrière les toasts d'activation (push, e-mail, PWA…).
 *  Chaque prompt possède un identifiant et sa propre cadence dans un store
 *  localStorage unique, afin de pouvoir réagir globalement aux changements
 *  de relation (achat, téléchargement de CV).
 *
 *  Règles web.dev appliquées par conception :
 *   - le refus est mémorisé et n'est pas relancé à chaque visite ;
 *   - une re-proposition n'a lieu qu'après un changement de relation ;
 *   - « Plus tard » → cooldown long, fermeture → cooldown plus long ;
 *   - max d'impressions puis pause (anti-harcèlement).
 *
 *  Conventions :
 *   - les impressions ne sont comptées qu'au moment réel de l'affichage ;
 *   - l'état vit par appareil/navigateur (cohérent avec les APIs web).
 * ─────────────────────────────────────────────────────────────────────────────
 */

const STORAGE_KEY = "soft_prompt_state_v1";

/** Événements « changement de relation » qui autorisent une re-proposition. */
export type RelationshipChange = "purchase" | "cv_completed";

/** Cadence d'un prompt. Les valeurs sont en millisecondes. */
export type SoftPromptOptions = {
  /** Cooldown après « Plus tard ». */
  laterCooldownMs: number;
  /** Cooldown après fermeture (X). */
  closeCooldownMs: number;
  /** Nombre maximum d'impressions avant pause. */
  maxImpressions: number;
  /** Pause une fois les impressions épuisées. */
  exhaustedCooldownMs: number;
  /** Changements de relation qui lèvent un refus pour ce prompt. */
  reactivateOn?: readonly RelationshipChange[];
};

type SoftPromptState = {
  impressions: number;
  lastShownAt: number | null;
  laterUntil: number | null;
  closeUntil: number | null;
  /** Refus explicite : bloquant jusqu'à un changement de relation. */
  declinedAt: number | null;
  /** Changements de relation enregistrés à la signature du refus. */
  declinedReactivateOn: readonly RelationshipChange[];
  /** Acceptation définitive : plus jamais affiché. */
  accepted: boolean;
};

type SoftPromptRegistry = Record<string, SoftPromptState>;

const DEFAULT_STATE: SoftPromptState = {
  impressions: 0,
  lastShownAt: null,
  laterUntil: null,
  closeUntil: null,
  declinedAt: null,
  declinedReactivateOn: [],
  accepted: false,
};

const load = (): SoftPromptRegistry => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Partial<SoftPromptRegistry>;
    if (!parsed || typeof parsed !== "object") return {};

    // Chaque entrée est complétée par les défauts à la lecture (getState).
    const registry: SoftPromptRegistry = {};
    for (const [promptId, state] of Object.entries(parsed)) {
      if (state && typeof state === "object") {
        registry[promptId] = { ...DEFAULT_STATE, ...state };
      }
    }
    return registry;
  } catch {
    return {};
  }
};

const save = (registry: SoftPromptRegistry) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(registry));
  } catch {
    // localStorage indisponible (mode privé en bloc, quota…) → on ignore.
  }
};

const getState = (registry: SoftPromptRegistry, promptId: string): SoftPromptState => ({
  ...DEFAULT_STATE,
  ...registry[promptId],
});

const patchState = (
  promptId: string,
  patch: (state: SoftPromptState) => SoftPromptState,
) => {
  const registry = load();
  registry[promptId] = patch(getState(registry, promptId));
  save(registry);
};

/** Retourne true si le prompt doit être proposé maintenant. */
export const shouldShowSoftPrompt = (
  promptId: string,
  options: SoftPromptOptions,
): boolean => getSoftPromptBlockReason(promptId, options) === null;

/**
 * Raison pour laquelle le prompt est bloqué, ou null s'il doit s'afficher.
 * Sert au diagnostic (log de la gate bloquante, cf. #116) — une pause n'a
 * jamais le droit d'être silencieuse.
 */
export type SoftPromptBlockReason =
  | "accepted"
  | "declined"
  | "exhausted"
  | "later"
  | "close";

export const getSoftPromptBlockReason = (
  promptId: string,
  options: SoftPromptOptions,
): SoftPromptBlockReason | null => {
  const state = getState(load(), promptId);
  if (state.accepted) return "accepted";
  if (state.declinedAt !== null) return "declined";

  const now = Date.now();

  // Impressions épuisées → pause longue (relance lente, non intrusive).
  if (state.impressions >= options.maxImpressions) {
    return now >= (state.lastShownAt ?? 0) + options.exhaustedCooldownMs
      ? null
      : "exhausted";
  }

  // Cohérence : tant qu'un cooldown actif court, on bloque l'affichage.
  if ((state.laterUntil ?? 0) > now) return "later";
  if ((state.closeUntil ?? 0) > now) return "close";
  return null;
};

/** Enregistre une impression — à appeler au moment réel de l'affichage. */
export const recordSoftPromptShown = (promptId: string) => {
  patchState(promptId, (state) => ({
    ...state,
    impressions: state.impressions + 1,
    lastShownAt: Date.now(),
  }));
};

/** « Plus tard » : re-proposition après le cooldown dédié. */
export const recordSoftPromptLater = (promptId: string, options: SoftPromptOptions) => {
  patchState(promptId, (state) => ({
    ...state,
    laterUntil: Date.now() + options.laterCooldownMs,
  }));
};

/** Fermeture (X) : re-proposition après le cooldown dédié. */
export const recordSoftPromptClosed = (promptId: string, options: SoftPromptOptions) => {
  patchState(promptId, (state) => ({
    ...state,
    closeUntil: Date.now() + options.closeCooldownMs,
  }));
};

/** Refus explicite : mémorisé, bloquant jusqu'à un changement de relation. */
export const recordSoftPromptDeclined = (
  promptId: string,
  options: SoftPromptOptions,
) => {
  patchState(promptId, (state) => ({
    ...state,
    declinedAt: Date.now(),
    declinedReactivateOn: options.reactivateOn ?? [],
  }));
};

/** Acceptation : le prompt n'est plus jamais affiché. */
export const recordSoftPromptAccepted = (promptId: string) => {
  patchState(promptId, (state) => ({ ...state, accepted: true }));
};

export const isSoftPromptAccepted = (promptId: string): boolean =>
  getState(load(), promptId).accepted;

export const isSoftPromptDeclined = (promptId: string): boolean =>
  getState(load(), promptId).declinedAt !== null;

/**
 * Changement de relation global : lève les refus des prompts concernés.
 * Les cooldowns et le compteur d'impressions restent inchangés.
 */
export const notifyRelationshipChange = (kind: RelationshipChange) => {
  const registry = load();
  let mutated = false;

  for (const [promptId, raw] of Object.entries(registry)) {
    const state = { ...DEFAULT_STATE, ...raw };
    if (state.declinedAt === null) continue;
    if (!state.declinedReactivateOn.includes(kind)) continue;
    registry[promptId] = { ...state, declinedAt: null, declinedReactivateOn: [] };
    mutated = true;
  }

  if (mutated) save(registry);
};

/** Utilitaire de test/réinitialisation (store local uniquement). */
export const resetSoftPrompt = (promptId: string) => {
  const registry = load();
  delete registry[promptId];
  save(registry);
};

/**
 * Migration (#116) : initialise un prompt depuis un état legacy.
 * Aucun effet si le prompt existe déjà (migration idempotente).
 * Retourne true si l'état a été semé.
 */
export const seedSoftPrompt = (
  promptId: string,
  seed: Partial<SoftPromptState>,
): boolean => {
  const registry = load();
  if (registry[promptId]) return false;
  registry[promptId] = { ...DEFAULT_STATE, ...seed };
  save(registry);
  return true;
};
